import { useCallback, useEffect, useRef, useState } from 'react';

/** Track whether a scrollport can move up/down; keep cues in sync with content size. */
export function useScrollCues(contentKey?: string | number) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollCues = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 2) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    setCanScrollUp(el.scrollTop > 2);
    setCanScrollDown(el.scrollTop < max - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sync = () => {
      requestAnimationFrame(updateScrollCues);
    };
    sync();
    el.addEventListener('scroll', updateScrollCues, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(sync)
        : null;
    ro?.observe(el);
    for (const child of Array.from(el.children)) {
      ro?.observe(child);
    }
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', updateScrollCues);
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [updateScrollCues, contentKey]);

  const scrollByPage = useCallback((dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      top: dir * Math.max(80, el.clientHeight * 0.7),
      behavior: 'smooth',
    });
  }, []);

  return { scrollRef, canScrollUp, canScrollDown, scrollByPage };
}
